import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import styles from './styles.module.scss'
import cn from 'classnames'
import errorLengthTextMessage from '../../assets/icons/error-length-text-message.svg'
import SupportHeader from './SupportHeader/SupportHeader'
import { useDispatch, useSelector } from 'react-redux'
import SupportMessage from './SupportMessage/SupportMessage'
import socket from '../../websocket'
import {
  editSupportMessage,
  replySupportMessage,
  resetTotalUndelivered,
  supportSetLoading,
} from '../../redux/support/support.action'
import Spinner from '../../UI/Spinner/Spinner'
import SupportOffline from './SupportOffline/SupportOffline'
import { getFirstName } from './helpers'
import SupportFooter from './SupportFooter/SupportFooter'
import { createItemRef } from '../../helpers/createItemRef'
import { getAllDates } from '../../helpers/timeHelper'
import { SupportDeleteConfirmation } from './SupportDeleteConfirmation/SupportDeleteConfirmation'

const Support = ({ className }) => {
  const [errorLengthText, setErrorLengthText] = useState(false)
  const [contextClickMessage, setContextClickMessage] = useState(null)
  const [scrollDirection, setScrollDirection] = useState({ up: true, down: false })
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const dispatch = useDispatch()

  const {
    messages,
    support_online,
    loading,
    undelivered_count,
    work,
    has_more_up,
    has_more_down,
    user,
    searchMessageId,
  } = useSelector(({ support }) => support)

  const messagesRef = useRef(null)
  const isNavigatingToMessageRef = useRef(false)

  const refs = createItemRef(messages)

  const dates = getAllDates(messages.map(({ date, id }) => ({ date, id })))
  const isRequestingRef = useRef(false)
  const lastScrollTopRef = useRef({ messages: 0, container: 0 })

  useEffect(() => {
    socket.emit('support.messages')

    socket.emit('support.chat.status')

    return () => {
      dispatch(supportSetLoading(true))
    }

    //eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (work) {
      socket.emit('support.chat.message.delivered')

      dispatch(resetTotalUndelivered())
    }

    //eslint-disable-next-line
  }, [undelivered_count])

  useEffect(() => {
    if (messagesRef.current && !loading) {
      if (searchMessageId) return
      messagesRef.current.scroll({ top: 0 })
    }
  }, [loading, messages, searchMessageId])

  useEffect(() => {
    if (!searchMessageId) return

    const target = refs?.[searchMessageId]?.current
    if (!target) return

    isNavigatingToMessageRef.current = true

    requestAnimationFrame(() => {
      target.scrollIntoView({ block: 'center' })
    })

    const timer = setTimeout(() => {
      isNavigatingToMessageRef.current = false
    }, 900)

    return () => clearTimeout(timer)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMessageId])

  const handleScroll = (e, source) => {
    if (isNavigatingToMessageRef.current) return

    const { scrollTop, scrollHeight } = e.currentTarget
    const prevScrollTop = lastScrollTopRef.current[source] ?? 0
    const delta = scrollTop - prevScrollTop
    const isUp = delta < 0
    const isDown = delta > 0

    lastScrollTopRef.current = { ...lastScrollTopRef.current, [source]: scrollTop }
    setScrollDirection({ up: isUp, down: isDown })

    if (!messages?.length) return
    if (loading || isRequestingRef.current) return

    if (has_more_up && scrollTop + scrollHeight - 800 < 0) {
      isRequestingRef.current = true
      dispatch(supportSetLoading(true))
      socket.emit('support.messages', {
        last_message_id: messages[messages.length - 1].id,
      })
      return
    }

    if (has_more_down && scrollTop - scrollHeight + 800 < 0) {
      isRequestingRef.current = true
      dispatch(supportSetLoading(true))
      socket.emit('support.messages', {
        reverse: true,
        last_message_id: messages[0].id,
      })
    }
  }

  const handleMessagesScroll = (e) => handleScroll(e, 'messages')
  const handleContainerScroll = (e) => handleScroll(e, 'container')

  const handleScrollDirection = useCallback(() => {}, [])

  const handleReply = useCallback(
    (id) => {
      dispatch(replySupportMessage(id))
      setContextClickMessage(null)
    },

    // eslint-disable-next-line
    []
  )

  const handleEdit = useCallback(
    (id) => {
      dispatch(editSupportMessage(id))
      setContextClickMessage(null)
    },

    // eslint-disable-next-line
    []
  )

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false)
    setDeletingId(null)
  }

  const handleDeleteConfirmed = () => {
    socket.emit('support.chat.delete.message', { message_id: deletingId })
    setDeletingId(null)
  }

  const handleDelete = (id) => {
    setIsConfirmOpen(true)
    setDeletingId(id)
  }

  const handleContextMenuClick = (e, id) => {
    e.preventDefault()

    setContextClickMessage(id)
  }

  const handleReplyClick = useCallback((id) => {
    isNavigatingToMessageRef.current = true
    dispatch(supportSetLoading(true))

    socket.emit('search.support.message', {
      page_size: 5,
      message_id: id,
    })

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!loading) {
      isRequestingRef.current = false
    }
  }, [loading])

  return (
    <div className={cn(styles.chat, { [className]: className, [styles.offline]: !work })}>
      {work && (
        <>
          <SupportHeader
            isChatStarted={user?.name}
            avatar={user?.avatar}
            name={getFirstName(user)}
            isOnline={support_online}
          />

          <div className={styles.messages} ref={messagesRef} onScroll={handleMessagesScroll} onWheel={handleScrollDirection}>
            <div className={styles.container} onScroll={handleContainerScroll}>
              {messages.map((message) => (
                <Fragment key={message.id}>
                  <SupportMessage
                    refs={refs[message.id]}
                    replyMessage={message.reply}
                    editMessage={message.edited}
                    onReply={handleReply}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onContextMenu={handleContextMenuClick}
                    setIsOpenMenu={setContextClickMessage}
                    isOpenMenu={contextClickMessage === message.id}
                    onReplyClick={handleReplyClick}
                    searchMessageId={searchMessageId}
                    {...message}
                  />

                  {dates.some((el) => el.id === message.id) && (
                    <div className={styles.message_day}>
                      <span>{dates.find((el) => el.id === message.id).value}</span>
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </div>
          {isConfirmOpen && <SupportDeleteConfirmation onTrue={handleDeleteConfirmed} onClose={handleCloseConfirm} />}
          <SupportFooter setErrorLengthText={setErrorLengthText} />

          {errorLengthText && (
            <div className={styles.maxLengthMessage}>
              <img src={errorLengthTextMessage} alt='error' />
              Уou entered the maximum number of characters!
            </div>
          )}
        </>
      )}

      {!work && !loading && <SupportOffline />}

      {loading && <Spinner />}
    </div>
  )
}

export default Support
